import { useEffect, useState } from "react";
import { Calendar, Clock, User } from "lucide-react";
import { Link } from "react-router-dom";

import {
  Appointment,
  fetchMentorAppointments,
  updateAppointmentStatus,
} from "../../data/appointments";

export function Appointments() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    async function loadAppointments() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchMentorAppointments();
        // Sort appointments by dateTime
        const sortedData = [...data].sort(
          (a, b) =>
            new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
        );
        setAppointments(sortedData);
      } catch (err) {
        console.error("Error loading appointments:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load appointments. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    }

    loadAppointments();
  }, []);

  const handleStatusUpdate = async (
    appointmentId: string,
    newStatus: "confirmed" | "declined"
  ) => {
    try {
      setUpdating(appointmentId);
      setError(null);
      const result = await updateAppointmentStatus(appointmentId, newStatus);

      if (!result.success) {
        throw new Error(result.error || "Failed to update appointment status");
      }

      // Update the local state
      setAppointments((currentAppointments) =>
        currentAppointments.map((appointment) =>
          appointment.id === appointmentId
            ? { ...appointment, status: newStatus }
            : appointment
        )
      );
    } catch (err) {
      console.error("Error updating appointment status:", err);
      setError("Failed to update appointment status. Please try again.");
    } finally {
      setUpdating(null);
    }
  };

  const getStatusColor = (status: Appointment["status"]) => {
    switch (status) {
      case "pending":
        return "bg-[#edf5ef] text-[#4CAF50]";
      case "confirmed":
        return "bg-[#edf5ef] text-[#4CAF50]";
      case "declined":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-[#4CAF50]"></div>
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <p className="font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600">No appointments found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <p className="font-medium">{error}</p>
        </div>
      )}

      <div className="rounded-xl border bg-white shadow-sm">
        <div className="divide-y">
          {appointments.map((appointment) => (
            <div key={appointment.id} className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-[#edf5ef] p-3">
                    <User className="size-5 text-[#4CAF50]" />
                  </div>
                  <div>
                    <h3 className="font-medium">{appointment.menteeName}</h3>
                    <p className="text-sm text-gray-600">
                      {appointment.menteeEmail}
                    </p>
                  </div>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(appointment.status)}`}
                >
                  {appointment.status.charAt(0).toUpperCase() +
                    appointment.status.slice(1)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="size-4" />
                    <span>
                      {new Date(appointment.dateTime).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <span>
                      {new Date(appointment.dateTime).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "numeric",
                        }
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {appointment.status === "pending" && (
                    <>
                      <button
                        onClick={() =>
                          handleStatusUpdate(appointment.id, "confirmed")
                        }
                        disabled={updating === appointment.id}
                        className="flex items-center gap-2 rounded-full bg-[#4CAF50] px-4 py-2 text-white transition-colors hover:bg-[#45A049] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {updating === appointment.id
                          ? "Confirming..."
                          : "Confirm"}
                      </button>
                      <button
                        onClick={() =>
                          handleStatusUpdate(appointment.id, "declined")
                        }
                        disabled={updating === appointment.id}
                        className="flex items-center gap-2 rounded-full bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {updating === appointment.id
                          ? "Declining..."
                          : "Decline"}
                      </button>
                    </>
                  )}
                  {appointment.status === "confirmed" && (
                    <Link
                      to={`/mentor/pre-session?sessionId=${appointment.id}&menteeName=${appointment.menteeName}`}
                      state={{
                        appointment: appointment,
                      }}
                      className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/80"
                    >
                      Go to Session
                    </Link>
                  )}
                  {appointment.status === "ended" && (
                    <Link
                      to={`/mentor/post-session?sessionId=${appointment.id}`}
                      state={{
                        appointment: appointment,
                      }}
                      className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-black transition-colors hover:bg-gray-200"
                    >
                      View Session Summary
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
